import { useState, useEffect, useCallback } from 'react'
import Api from '@vacationtracker/shared/services/api'
import { SurveyConfig, SurveyQuestion, SurveyResponse } from './types'

const VOTITO_PANELIST_API = 'https://api.votito.com/public'
const SURVEY_STORAGE_KEY = 'vt-survey-completed'

interface UseVotitoSurveyParams {
  surveyId: string
  enabled?: boolean
}

interface UseVotitoSurveyResult {
  survey: SurveyConfig | null
  isLoading: boolean
  isCompleted: boolean
  hasLoaded: boolean
  error: string | null
  submitResponses: (responses: SurveyResponse[]) => Promise<boolean>
  dismiss: () => Promise<void>
}

const getSurveyCompletedIds = (): string[] => {
  try {
    const stored = localStorage.getItem(SURVEY_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

const markSurveyCompleted = (surveyId: string): void => {
  try {
    const ids = getSurveyCompletedIds()
    if (!ids.includes(surveyId)) {
      ids.push(surveyId)
      localStorage.setItem(SURVEY_STORAGE_KEY, JSON.stringify(ids))
    }
  } catch {
    // ignore
  }
}

// Votito sends "single"/"multiple" for choice questions; the other names are kept for older surveys.
const CHOICE_QUESTION_TYPES = ['single', 'multiple', 'single-choice', 'multiple-choice', 'multi-choice']

const hasLabelledOptions = (q: any): boolean => (q.answerOptions || []).some((opt: any) => Boolean(opt.label || opt.optionText || opt.text))

// Kano questions need one answer from each of two option groups, which the survey UI does not support.
const isUnsupportedQuestion = (q: any): boolean => (q.questionType || q.type) === 'kano'

export const mapVotitoQuestions = (surveyData: any): SurveyQuestion[] => {
  if (!surveyData?.questions) return []

  return surveyData.questions
    .filter((q: any) => !isUnsupportedQuestion(q))
    .map((q: any) => {
      const id = q.questionId || q.id
      const text = q.questionText || q.text || q.title || ''
      const questionType = q.questionType || q.type
      const optional = q.optional ? { optional: true } : {}

      if (questionType === 'likert') {
        const answerOptions = q.answerOptions || []
        return {
          id,
          text,
          type: 'scale' as const,
          ...optional,
          scaleMin: 1,
          scaleMax: answerOptions.length || 7,
          scaleMinLabel: q.scaleLabels?.min,
          scaleMaxLabel: q.scaleLabels?.max,
          options: answerOptions.map((opt: any) => ({
            id: opt.optionId || opt.id,
            label: opt.label || opt.text || '',
            value: opt.value ?? 0,
          })),
        }
      }

      if (questionType === 'open-ended') {
        return { id, text, type: 'text' as const, ...optional }
      }

      // Votito's "text" question is display-only: it is shown but never answered or submitted.
      if (questionType === 'text') {
        return { id, text, type: 'statement' as const }
      }

      // An unknown type that still comes with labelled options is a choice question, never a free-text box.
      if (CHOICE_QUESTION_TYPES.includes(questionType) || hasLabelledOptions(q)) {
        const isMulti = q.responseType === 'independent'
        return {
          id,
          text,
          type: isMulti ? ('multi-choice' as const) : ('single-choice' as const),
          ...optional,
          options: (q.answerOptions || []).map((opt: any) => ({
            id: opt.optionId || opt.id,
            label: opt.label || opt.optionText || opt.text || '',
            value: opt.value ?? 0,
          })),
        }
      }

      // Fallback
      return { id, text, type: 'text' as const, ...optional }
    })
}

const FINAL_SUBMIT_ERRORS = ['session-already-completed', 'panelist-session-exists', 'survey-not-available-for-voting']

type VotitoVotes = Record<string, { optionIds: string[]; explanation?: string }>

export const isBlankAnswer = (value: SurveyResponse['value'] | undefined): boolean =>
  value === undefined || (Array.isArray(value) ? value.length === 0 : String(value).trim() === '')

// Shapes answers as the Panelist API expects; statements and blank or skipped answers are left out.
export const buildVotitoVotes = (questions: SurveyQuestion[], responses: SurveyResponse[]): VotitoVotes => {
  const votes: VotitoVotes = {}
  responses.forEach((r) => {
    const question = questions.find((q) => q.id === r.questionId)
    if (!question || question.type === 'statement' || isBlankAnswer(r.value)) return

    if (question.type === 'text') {
      votes[r.questionId] = { optionIds: [], explanation: String(r.value).trim() }
    } else if (question.type === 'multi-choice') {
      votes[r.questionId] = { optionIds: Array.isArray(r.value) ? r.value : [String(r.value)] }
    } else if (question.type === 'scale' && question.options?.length) {
      const option = question.options[Number(r.value) - (question.scaleMin ?? 1)]
      votes[r.questionId] = { optionIds: [option?.id ?? String(r.value)] }
    } else {
      votes[r.questionId] = { optionIds: [String(r.value)] }
    }
  })
  return votes
}

export const useVotitoSurvey = ({ surveyId, enabled = true }: UseVotitoSurveyParams): UseVotitoSurveyResult => {
  const [survey, setSurvey] = useState<SurveyConfig | null>(null)
  const [publicKey, setPublicKey] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled || !surveyId) return

    // Check localStorage cache first
    const completedIds = getSurveyCompletedIds()
    if (completedIds.includes(surveyId)) {
      setIsCompleted(true)
      setHasLoaded(true)
      return
    }

    const loadSurvey = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Step 1: Get publicKey from our backend
        const tokenResponse = await Api.get<{ publicKey: string }>(`/core/survey-token?surveyId=${encodeURIComponent(surveyId)}`)
        const key = tokenResponse.publicKey
        setPublicKey(key)

        // Step 2: Load survey questions from Votito Panelist API
        const surveyResponse = await fetch(`${VOTITO_PANELIST_API}/survey`, {
          headers: { Authorization: key },
        })

        // 403 is an invalid token, not a finished survey: hide it for now without remembering it as completed.
        if (surveyResponse.status === 403) {
          setIsCompleted(true)
          return
        }

        // 404 means the survey does not exist or is not open, so there is simply nothing to show.
        if (surveyResponse.status === 404) {
          return
        }

        if (!surveyResponse.ok) {
          throw new Error(`Failed to load survey: ${surveyResponse.status}`)
        }

        const surveyData = await surveyResponse.json()
        const questions = mapVotitoQuestions(surveyData)

        if (questions.length > 0) {
          setSurvey({
            id: surveyId,
            title: surveyData.title || 'Survey',
            questions,
          })
        }
      } catch (err) {
        console.error('Failed to load Votito survey:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setIsLoading(false)
        setHasLoaded(true)
      }
    }

    loadSurvey()
  }, [surveyId, enabled])

  const submitResponses = useCallback(
    async (responses: SurveyResponse[]): Promise<boolean> => {
      if (!publicKey || !survey) return false

      try {
        const votitoResponses = buildVotitoVotes(survey.questions, responses)

        // Votito rejects an empty votes object; with only skipped optional questions there is nothing to record.
        if (Object.keys(votitoResponses).length === 0) {
          markSurveyCompleted(surveyId)
          setIsCompleted(true)
          return true
        }

        const response = await fetch(`${VOTITO_PANELIST_API}/survey`, {
          method: 'POST',
          headers: {
            Authorization: publicKey,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            votes: votitoResponses,
            isFinal: true,
          }),
        })

        if (!response.ok) {
          // Already answered (maybe in another browser) or closed: retrying can never succeed, so treat it as done.
          const body = await response.text()
          if (FINAL_SUBMIT_ERRORS.some((code) => body.includes(code))) {
            markSurveyCompleted(surveyId)
            setIsCompleted(true)
            return true
          }
          throw new Error(`Failed to submit survey: ${response.status}`)
        }

        markSurveyCompleted(surveyId)
        setIsCompleted(true)
        return true
      } catch (err) {
        console.error('Failed to submit survey:', err)
        return false
      }
    },
    [publicKey, surveyId, survey]
  )

  const dismiss = useCallback(async () => {
    if (publicKey) {
      try {
        await fetch(`${VOTITO_PANELIST_API}/survey/dismiss`, {
          method: 'POST',
          headers: { Authorization: publicKey },
        })
      } catch (err) {
        console.error('Failed to dismiss survey:', err)
      }
    }
  }, [publicKey, surveyId])

  return { survey, isLoading, isCompleted, hasLoaded, error, submitResponses, dismiss }
}
